/**
 * Product preview - Figma `6377:88896`.
 *
 * Two panels side by side: product screenshots on a pale green ground, and a photograph
 * with a caption card sitting over its lower edge.
 *
 * **`media` is repeatable here** - the design has two panels and the model held one. The
 * first image is the product shot and is shown whole (`object-contain`, so a screenshot
 * is never cropped); the second is the photograph and fills its panel.
 *
 * This is one of the compositions the design draws by hand: in Figma the left panel is
 * three separate UI fragments arranged by eye. An editor supplies one exported image
 * instead, which is the only version of this a content model can hold.
 */
import { SectionShell } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import type { ProductPreviewSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

export default function ProductPreview({ data }: { data: ProductPreviewSection }) {
  const images = (data.media ?? []).filter((item) => item?.image);
  const [product, photo] = images;
  const captions = data.captions ?? [];

  return (
    <SectionShell settings={data.settings} name="product-preview">
      {data.heading ? <h2 className="max-w-3xl text-h3 text-ink lg:text-h2">{data.heading}</h2> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {product ? (
          <figure className="m-0 flex min-h-[320px] items-center justify-center rounded-l bg-green-50 p-6">
            <Image
              media={product.image}
              alt={product.alt ?? product.image.alternativeText ?? ''}
              sizes={SIZES.half}
              className="max-h-full w-full object-contain"
            />
          </figure>
        ) : null}

        {photo ? (
          <div className="relative min-h-[320px] overflow-hidden rounded-l bg-surface-sunken p-5">
            <Image
              media={photo.image}
              alt={photo.alt ?? photo.image.alternativeText ?? ''}
              sizes={SIZES.half}
              className="absolute inset-0 h-full w-full object-cover"
            />

            {captions.length ? (
              <div className="relative mt-auto flex h-full flex-col justify-end">
                <div className="flex items-center justify-between gap-4 rounded-s bg-surface p-5">
                  <p className="text-body-sm text-ink-strong">{captions.map((c) => c.label).join(' ')}</p>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-s bg-green-500 text-white">
                    <Icon name="verified" size={20} />
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}
